import { Component, OnDestroy, OnInit, Renderer2 } from "@angular/core";
import { AuthService } from "../auth/auth.service";
import { Router } from "@angular/router";
import { AppFilter } from "../../models/app-filter.model";
import { TranslateService } from '@ngx-translate/core';
import { deleteCookieLocal } from "app/utils/cookie.utils";
import { hasPerm, Permission } from "../auth/permission.enum";
import { AbTenant } from "app/abstract/full-pages/admin/tenant.abstract";
import { AbLogin } from "app/abstract/content-pages/login/login.abstract";
import { AbPessoa } from "app/abstract/full-pages/pessoa/pessoa.abstract";
import { AbConfigSkin } from "app/abstract/full-pages/config-skin/config-skin.abstract";
import { AbToastr } from "app/abstract/services/toastr.abstract";
import { AbLocale } from "app/abstract/full-pages/locale/locale.abstract";
import { AbConta } from "app/abstract/full-pages/conta/conta.abstract";
import { Subject, takeUntil } from "rxjs";

@Component({
  selector: "app-navbar",
  templateUrl: "./navbar.component.html",
})
export class NavbarComponent implements OnDestroy, OnInit {
  filter: AppFilter = new AppFilter();
  contas = [];
  contasAux = [];
  contaSelecionadaNome: string;
  fotoPessoa: string = null;
  nomePessoa: string;
  locales: any = [];
  executar = 0;

  private KEY_CONFIGURACOES = "configuracaoConta";
  private readonly destroy$ = new Subject();

  constructor(
    public tenantService: AbTenant,
    private routes: Router,
    private authService: AuthService,
    private loginService: AbLogin,
    private pessoaService: AbPessoa,
    private configSkinService: AbConfigSkin,
    private toastr: AbToastr,
    private renderer: Renderer2,
    private translate: TranslateService,
    private localeService: AbLocale,
    private contaService: AbConta
  ) { }

  private removerConfiguracoesContaAnteriorStorage() {
    const configuracoes = JSON.parse(sessionStorage.getItem(this.KEY_CONFIGURACOES));
    if (configuracoes) {
      sessionStorage.removeItem(this.KEY_CONFIGURACOES);
    }
  }

  ngOnInit() {
    this.authService.saveCarregarConta(false);
    this.carregarLocales();
    this.carregarContas();
    if (this.tenantService.getTenantStorage() != null) {
      this.setContaSelecionada();
      if (!this.isIntegradorOuDistribuidor()) {
        this.carregarPessoaLogada();
      }
    }
  }

  logout() {
    this.configSkinService.getSkinCor().pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data && data.cor) {
        this.renderer.removeClass(document.body, "body-" + data.cor);
      }
    });
    this.authService.logout();
    this.routes.navigate(["/pages/login"]);
  }

  sairDaConta() {
    this.authService.cleanFilter();
    sessionStorage.removeItem("psimHabilitado");
    this.authService.cleanTimeZoneStorage();
    this.tenantService.limparTenantStorage();
    this.tenantService.limparTenantSchemaStorage();
    this.tenantService.salvarNomeTenantStorage(null);
    deleteCookieLocal();
    this.loginService.refreshToken(null);
    this.contaSelecionadaNome = undefined;
  }

  existeTenant() {
    return this.tenantService.getNomeTenantStorage() != null;
  }

  isIntegrador(): boolean {
    const user = this.authService.getUsuarioStorage();
    return user && user.integrador;
  }

  isIntegradorOuDistribuidor(): boolean {
    const user = this.authService.getUsuarioStorage();
    return user && (user.integrador || user.contas.length == 0);
  }

  exibirSelectConta(): boolean {
    const user = this.authService.getUsuarioStorage();
    if (this.existeTenant() && user) {
      this.executar = this.executar + 1;
      this.validarFotoStorage();
    }
    return user && user.contas && user.contas.length > 1;
  }

  isDistribuidor(): boolean {
    const user = this.authService.getUsuarioStorage();

    return user && (!user.contas || user.contas.length == 0);
  }

  exibirNomeConta(): boolean {
    if (!this.fotoPessoa) {
      this.setContaSelecionada();
      this.validarFotoStorage();
    }
    return this.routes.url.indexOf("add-conta") < 1 && this.tenantService.getNomeTenantStorage() != null;
  }

  exibirMenuMeusDados(): boolean {
    const user = this.authService.getUsuarioStorage();
    return user && user.contas && user.contas.length >= 1;
  }

  exibirMenuConfiguracoes(): boolean {
    const user = this.authService.getUsuarioStorage();
    return user && hasPerm(Permission.CONFIGURACOES_UPDATE || Permission.CONFIGURACOES_CREATE);
  }

  carregarContas() {
    if (this.filter.str1) {
      this.contas = this.contasAux.filter(i => i.nomeConta.toUpperCase().indexOf(this.filter.str1.toUpperCase()) > -1);
      if (this.contasAux.length > 0) {
        return;
      }
    }
    this.contas = this.contasAux.filter(i => i.nomeConta.toUpperCase());

    if (this.isIntegrador() && hasPerm(Permission.CONTA_READ)) {
      this.contas = this.contasAux.filter(i => i.nomeConta.toUpperCase());
      this.tenantService.getAllTenantsIntegrador().pipe(takeUntil(this.destroy$)).subscribe(
        (contas: any) => {
          this.contas = [];
          this.contasAux = [];
          contas.filter((item) => !item.tipoLicenca?.toUpperCase().includes("ACCESS")).forEach(element => {
            this.contas.push(element);
            this.contasAux.push(element);
          });
        });
      return;
    }

    const user = this.authService.getUsuarioStorage();
    if (user && user.contas) {
      this.contas = user.contas;
      this.contasAux = user.contas.filter((item) => !item.tipoLicenca?.toUpperCase().includes("ACCESS"));
      this.contas.sort(function (a, b) {
        return a.nomeConta < b.nomeConta ? -1 : a.nomeConta > b.nomeConta ? 1 : 0;
      });
    }
  }

  entrarNaConta(tenant) {
    this.authService.cleanTokenLGPD();
    if (tenant.acessoBloqueado) {
      this.toastr.warning(this.translate.instant("O acesso à conta está bloqueado temporariamente."))
      return
    }

    deleteCookieLocal();
    this.contaService.cleanToken();
    this.tenantService.limparTenantSchemaStorage();
    this.tenantService.salvarTenantStorage(tenant);
    this.tenantService.salvarNomeTenantStorage(tenant.nomeConta);
    this.loginService.refreshToken(tenant);
    this.setContaSelecionada();
    this.carregarPessoaLogada();
  }

  setContaSelecionada() {
    this.removerConfiguracoesContaAnteriorStorage();
    const tenant = this.tenantService.getTenantStorage();
    const nome = this.tenantService.getNomeTenantStorage();
    if (tenant && nome) {
      this.contaSelecionadaNome = tenant.nomeConta;
    }
  }

  carregarLocales() {
    this.localeService.getLocales().pipe(takeUntil(this.destroy$)).subscribe(locales => {
      if (locales) {
        this.locales = locales
      }
    })
  }

  carregarPessoaLogada() {
    const user = this.authService.getUsuarioStorage();
    if (user && user.contas) {
      this.pessoaService.getPessoaLogada().pipe(takeUntil(this.destroy$)).subscribe(
        (data: any) => {
          if (!data) {
            return;
          }
          if (data.foto) {
            this.fotoPessoa = 'data:image/jpeg;base64,' + data.foto;
            this.authService.saveFotoPessoaLogada(this.fotoPessoa);
          } else {
            this.authService.saveFotoPessoaLogada(null);
            this.fotoPessoa = undefined;
          }
          this.executar = this.executar + 1;
          this.nomePessoa = data.nome;
          if (data.sobrenome) {
            this.nomePessoa = this.nomePessoa + " " + data.sobrenome;
          }
        }
      )
      return;
    }
    this.fotoPessoa = undefined;
    this.nomePessoa = undefined;
    return;
  }

  validarFotoStorage() {
    const foto = this.authService.getFotoPessoaLogada();
    if (foto) {
      this.fotoPessoa = foto;
      return true;
    }

    if (this.executar === 1) {
      this.carregarPessoaLogada();
      this.executar = 2;
    }

    this.fotoPessoa = undefined;
    return false;
  }

  getPessoaLogada() {
    return this.authService.getFotoPessoaLogada();
  }

  exibirContas(): boolean {
    if (this.isIntegrador()) {
      return this.contasAux.length > 1;
    } else {
      return this.exibirSelectConta() || this.exibirNomeConta();
    }
  }

  carregarDados() {
    if (this.authService.getCarregarConta()) {
      this.carregarContas();
      this.authService.saveCarregarConta(false);
    }
    return true;
  }

  setUserLocale(locale) {
    this.translate.use(`${locale.language}_${locale.country}`)
    this.localeService.alterarLocaleUsuario(locale).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.toastr.success(this.translate.instant("Sucesso"), this.translate.instant("Idioma alterado com sucesso!"))
    }, () => {
      this.toastr.warning(this.translate.instant("Erro ao salvar alteração no idioma"))
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

}
